import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useCredits } from '../../contexts/CreditsContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { authFetch } from '../../lib/authFetch';
import NotifPanel from './NotifPanel';
import BugReportModal from './BugReportModal';

const SALES_QUOTES = [
  "Every 'no' gets you one step closer to a 'yes.'",
  "Your attitude, not your aptitude, determines your altitude.",
  "Make it happen. Shock everyone.",
  "The best time to close a deal was yesterday. The second best is right now.",
  "Success is not given. It's earned on the floor, every single day.",
  "Don't watch the clock — do what it does. Keep going.",
  "You don't close a sale, you open a relationship.",
  "A winner is just a loser who tried one more time.",
  "The harder you work, the luckier you get.",
  "Confidence is not 'they will like me.' It's 'I'll be fine if they don't.'",
  "Dream big. Hustle harder. Close deals.",
  "Your pipeline is only as strong as your follow-up.",
  "Be so good they can't ignore you.",
  "The secret of getting ahead is getting started.",
  "Pain is temporary. Commission is forever.",
  "Stop selling. Start solving.",
  "Champions aren't made in the ring — they're just recognized there.",
  "Every call is a chance to change someone's business.",
  "Rejection is redirection. Keep dialing.",
  "Your next deal is already out there. Go find it.",
  "Outwork everyone. Outhustle everything.",
  "Sales is the art of turning fear into fuel.",
  "One more call. One more email. One more deal.",
  "The difference between try and triumph is a little 'umph.'",
  "Fortune favors the bold — and the persistent.",
  "Speak less. Listen more. Close faster.",
  "Your competition isn't working as hard as you. Prove it.",
  "Discipline is choosing what you want most over what you want now.",
  "Be the energy you want to attract in every room.",
  "First-rate closers make first-rate calls. Go be first-rate.",
  "Every morning you wake up is another chance to crush it.",
  "Customers don't care about your product — they care about their problem.",
  "Success is the sum of small efforts repeated day in and day out.",
  "Act as if what you do makes a difference — because it does.",
  "The goal is not to be better than the other guy. It's to be better than you were yesterday.",
];

function MotivationalQuote() {
  const [visible, setVisible] = useState(false);
  const quote = useMemo(() => {
    const day = Math.floor(Date.now() / 86400000);
    return SALES_QUOTES[day % SALES_QUOTES.length];
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="mx-3 mt-2 mb-1">
      <div
        className="rounded-xl border border-yellow-500/15 bg-yellow-500/5 px-3 py-2.5 transition-all duration-700"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(6px)' }}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px]">✨</span>
          <span className="text-[9px] font-black uppercase tracking-[0.12em] text-yellow-500/70">Daily Fuel</span>
        </div>
        <p className="text-[11px] italic text-yellow-400/80 leading-snug">
          "{quote}"
        </p>
      </div>
    </div>
  );
}

const navGroups = {
  client: [
    {
      label: 'Main',
      items: [
        { label: 'Dashboard', path: '/dashboard/client', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'Services', path: '/dashboard/client/services', icon: 'M19 11H7m0 0l5-5m-5 5l5 5' },
        { label: 'My Requests', path: '/dashboard/client/requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
        { label: 'My Orders', path: '/dashboard/client/jobs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { label: 'My Projects', path: '/dashboard/client/projects', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'Messages', path: '/dashboard/client/messages', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
      ],
    },
    {
      label: 'Files & Reports',
      items: [
        { label: 'Files', path: '/dashboard/client/files', icon: 'M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z' },
        { label: 'Reports', path: '/dashboard/client/reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      ],
    },
    {
      label: 'Billing & Account',
      items: [
        { label: 'Plans', path: '/dashboard/client/plans', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
        { label: 'Billing', path: '/dashboard/client/billing', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
        { label: 'My Perks', path: '/dashboard/client/subscriptions', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
        { label: 'Refer & Earn', path: '/dashboard/client/referral', icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' },
        { label: 'Profile', path: '/dashboard/client/profile', icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
      ],
    },
  ],
  sales_rep: [
    {
      label: 'Main',
      items: [
        { label: 'Dashboard', path: '/dashboard/sales', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'Lead Pipeline', path: '/dashboard/sales/pipeline', notifBadge: true, icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'AI Prospects', path: '/dashboard/sales/ai-prospects', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
        { label: 'My Clients', path: '/dashboard/sales/clients', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'Requests', path: '/dashboard/sales/requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { label: 'Quotes', path: '/dashboard/sales/quotes', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { label: 'Custom Offers', path: '/dashboard/sales/offers', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z' },
      ],
    },
    {
      label: 'Revenue',
      items: [
        { label: 'Commissions', path: '/dashboard/sales/commissions', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'My Payouts', path: '/dashboard/sales/payout-dashboard', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
      ],
    },
    {
      label: 'Learning Centre',
      items: [
        { label: 'Learning Centre', path: '/dashboard/sales/learning', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
      ],
    },
    {
      label: 'Account',
      items: [
        { label: 'Messages', path: '/dashboard/sales/messages', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
        { label: 'Pricing & Credits', path: '/dashboard/sales/pricing', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Profile', path: '/dashboard/sales/profile', icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
      ],
    },
  ],
  contractor: [
    {
      label: 'Main',
      items: [
        { label: 'Dashboard', path: '/dashboard/contractor', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'My Jobs', path: '/dashboard/contractor/jobs', notifBadge: true, icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
        { label: 'My Projects', path: '/dashboard/contractor/projects', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'Submissions', path: '/dashboard/contractor/submissions', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
      ],
    },
    {
      label: 'Finance',
      items: [
        { label: 'Payouts', path: '/dashboard/contractor/payouts', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Subscriptions', path: '/dashboard/contractor/subscriptions', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
      ],
    },
    {
      label: 'Work',
      items: [
        { label: 'Messages', path: '/dashboard/contractor/messages', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
        { label: 'Files', path: '/dashboard/contractor/files', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
      ],
    },
    {
      label: 'Account',
      items: [
        { label: 'Profile', path: '/dashboard/contractor/profile', icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
      ],
    },
  ],
  contractor_admin: [
    {
      label: 'Contractors Admin',
      items: [
        { label: 'Control Panel', path: '/dashboard/contractor-admin', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
        { label: 'Manage Contractors', path: '/dashboard/contractor-admin/contractors', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'All Jobs', path: '/dashboard/contractor-admin/jobs', notifBadge: true, icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
        { label: 'Payout Queue', path: '/dashboard/contractor-admin/payouts', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
      ],
    },
    {
      label: 'Account',
      items: [
        { label: 'Profile', path: '/dashboard/contractor-admin/profile', icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
      ],
    },
  ],
  admin: [
    {
      label: 'Main',
      items: [
        { label: 'Dashboard', path: '/dashboard/admin', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'User Manager', path: '/dashboard/admin/users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
        { label: 'Invites', path: '/dashboard/admin/invites', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
        { label: 'Promo Codes', path: '/dashboard/admin/promo-codes', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
        { label: 'Bug Reports', path: '/dashboard/admin/bug-reports', icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z' },
        { label: 'Error Tracker', path: '/dashboard/admin/error-tracker', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
        { label: 'Feedback', path: '/dashboard/admin/feedback', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
      ],
    },
    {
      label: 'Operations',
      items: [
        { label: 'Leads', path: '/dashboard/admin/leads', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'Projects', path: '/dashboard/admin/projects', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'Jobs', path: '/dashboard/admin/jobs', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
        { label: 'Services', path: '/dashboard/admin/services', icon: 'M19 11H7m0 0l5-5m-5 5l5 5' },
        { label: 'Live Chats', path: '/dashboard/admin/live-chats', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
        { label: 'Contracts', path: '/dashboard/admin/contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { label: 'NDAs', path: '/dashboard/admin/ndas', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
        { label: 'Case Studies', path: '/dashboard/admin/case-studies', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z' },
        { label: 'Google Reviews', path: '/dashboard/admin/google-reviews', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
        { label: 'Contractor Apps', path: '/dashboard/admin/contractor-applications', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
        { label: 'Phone Numbers', path: '/dashboard/admin/phone-numbers', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
        { label: 'Demo Themes', path: '/dashboard/admin/themes', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
      ],
    },
    {
      label: 'Finance',
      items: [
        { label: 'Payouts', path: '/dashboard/admin/payouts', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'Commission Payouts', path: '/dashboard/admin/commission-payouts', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Commission Approvals', path: '/dashboard/admin/commission-approvals', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
        { label: 'Finder Commissions', path: '/dashboard/admin/finder-commissions', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
        { label: 'Rep Subscriptions', path: '/dashboard/admin/subscriptions', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
        { label: 'Credit Manager', path: '/dashboard/admin/credit-manager', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
      ],
    },
    {
      label: 'Analytics',
      items: [
        { label: 'Reports', path: '/dashboard/admin/reports', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { label: 'Profitability', path: '/dashboard/admin/profitability', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
        { label: 'Site Analytics', path: '/dashboard/admin/site-analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
        { label: 'Funnel Analytics', path: '/dashboard/admin/funnel-analytics', icon: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12' },
        { label: 'Client LTV', path: '/dashboard/admin/client-ltv', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
        { label: 'Churn Prediction', path: '/dashboard/admin/churn', icon: 'M13 17h8m0 0V9m0 8l-8-8-4 4-6-6' },
      ],
    },
    {
      label: 'Prospect Tools',
      items: [
        { label: 'AI Prospects', path: '/dashboard/admin/ai-prospects', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
        { label: 'Google Finder', path: '/dashboard/admin/prospect-finder', icon: 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z' },
        { label: 'Apollo Finder', path: '/dashboard/admin/apollo-finder', icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Hunter Finder', path: '/dashboard/admin/hunter-finder', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
        { label: 'Registry Finder', path: '/dashboard/admin/registry-finder', icon: 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z' },
        { label: 'Data Approvals', path: '/dashboard/admin/data-approvals', approvalBadge: true, icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Lead Verifications', path: '/dashboard/admin/lead-verifications', verifyBadge: true, icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
        { label: 'Contact Reports', path: '/dashboard/admin/contact-reports', reportBadge: true, icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9' },
      ],
    },
    {
      label: 'AI Sales Agent',
      items: [
        { label: 'Agent Dashboard', path: '/dashboard/admin/sales-agent', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
        { label: 'Call History', path: '/dashboard/admin/sales-agent/calls', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
        { label: 'Live Monitor', path: '/dashboard/admin/sales-agent/live-call', icon: 'M15 10l4.553-2.277A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z' },
        { label: 'SMS Conversations', path: '/dashboard/admin/sales-agent/sms', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
        { label: 'Campaigns', path: '/dashboard/admin/sales-agent/campaigns', icon: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z' },
        { label: 'Intelligence Hub', path: '/dashboard/admin/sales-agent/intelligence', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
        { label: 'Voice Settings', path: '/dashboard/admin/sales-agent/voice-settings', icon: 'M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' },
        { label: 'Call Calendar', path: '/dashboard/admin/sales-agent/calendar', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
        { label: 'SMS Calendar', path: '/dashboard/admin/sales-agent/sms-calendar', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
      ],
    },
    {
      label: 'Contractors',
      items: [
        { label: 'Overview', path: '/dashboard/admin/contractors', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'Jobs', path: '/dashboard/admin/contractors/jobs', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
        { label: 'Subscriptions', path: '/dashboard/admin/contractors/subscriptions', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { label: 'Control Panel', path: '/dashboard/admin/contractor-admin', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
        { label: 'Manage Contractors', path: '/dashboard/admin/contractor-admin/contractors', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'All Jobs', path: '/dashboard/admin/contractor-admin/jobs', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
        { label: 'Payout Queue', path: '/dashboard/admin/contractor-admin/payouts', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
      ],
    },
    {
      label: 'Sales Rep View',
      items: [
        { label: 'Lead Pipeline', path: '/dashboard/sales/pipeline', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'Learning Centre', path: '/dashboard/sales/learning', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
        { label: 'Quotes', path: '/dashboard/sales/quotes', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { label: 'Custom Offers', path: '/dashboard/sales/offers', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z' },
        { label: 'Rep Requests', path: '/dashboard/sales/requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { label: 'Rep Clients', path: '/dashboard/sales/clients', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'Rep Commissions', path: '/dashboard/sales/commissions', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Rep Payout Dash', path: '/dashboard/sales/payout-dashboard', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
        { label: 'Rep Messages', path: '/dashboard/sales/messages', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
      ],
    },
    {
      label: 'Client View',
      items: [
        { label: 'Client Dashboard', path: '/dashboard/client', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'Client Services', path: '/dashboard/client/services', icon: 'M19 11H7m0 0l5-5m-5 5l5 5' },
        { label: 'Client Requests', path: '/dashboard/client/requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
        { label: 'Client Orders', path: '/dashboard/client/jobs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { label: 'Client Projects', path: '/dashboard/client/projects', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'Client Files', path: '/dashboard/client/files', icon: 'M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z' },
        { label: 'Client Billing', path: '/dashboard/client/billing', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
        { label: 'Client Plans', path: '/dashboard/client/plans', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
        { label: 'Client Messages', path: '/dashboard/client/messages', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
      ],
    },
    {
      label: 'Contractor View',
      items: [
        { label: 'Contractor Dash', path: '/dashboard/contractor', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z' },
        { label: 'Contractor Jobs', path: '/dashboard/contractor/jobs', icon: 'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4' },
        { label: 'Contractor Projects', path: '/dashboard/contractor/projects', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
        { label: 'Contractor Files', path: '/dashboard/contractor/files', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
      ],
    },
    {
      label: 'Platform',
      items: [
        { label: 'Security', path: '/dashboard/admin/security', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
        { label: 'System Health', path: '/dashboard/admin/system-health', icon: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18' },
        { label: 'Feature Controls', path: '/dashboard/admin/feature-controls', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
        { label: 'Webhook Errors', path: '/dashboard/admin/webhook-errors', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
        { label: 'Payment Tests', path: '/dashboard/admin/payment-tests', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
      ],
    },
    {
      label: 'Account',
      items: [
        { label: 'Profile', path: '/dashboard/admin/profile', icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'Pricing & Credits', path: '/dashboard/admin/pricing', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
      ],
    },
  ],
};

const roleLabel = {
  admin: 'Admin',
  sales_rep: 'Sales Rep',
  client: 'Client',
  contractor: 'Contractor',
  contractor_admin: 'Contractors Admin',
};

const roleBasePath = {
  sales_rep: 'sales',
  contractor_admin: 'contractor-admin',
};

function DashboardNav({ role, collapsed, onClose, user, profile, showUserCard, onSignOut }) {
  const groups = navGroups[role] || navGroups.client;
  const { toggleTheme, isDark } = useTheme();
  const { credits, plan, planInfo, unlimited, loading: creditsLoading } = useCredits();
  const navigate = useNavigate();

  const { count: notifCount } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [dataApprovalCount, setDataApprovalCount] = useState(0);
  const [verifyCount, setVerifyCount] = useState(0);

  useEffect(() => {
    if (role !== 'admin') return;
    let alive = true;
    const fetchCount = () => {
      authFetch('/api/admin/discoveries/count')
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (alive && d?.count != null) setDataApprovalCount(d.count); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(interval); };
  }, [role]);

  useEffect(() => {
    if (role !== 'admin') return;
    let alive = true;
    const fetchVerifyCount = () => {
      authFetch('/api/admin/leads/pending-verifications/count')
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (alive && d?.count != null) setVerifyCount(d.count); })
        .catch(() => {});
    };
    fetchVerifyCount();
    const interval = setInterval(fetchVerifyCount, 60000);
    return () => { alive = false; clearInterval(interval); };
  }, [role]);

  const [reportCount, setReportCount] = useState(0);
  useEffect(() => {
    if (role !== 'admin') return;
    let alive = true;
    const fetchReportCount = () => {
      authFetch('/api/admin/contact-reports/count')
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (alive && d?.count != null) setReportCount(d.count); })
        .catch(() => {});
    };
    fetchReportCount();
    const interval = setInterval(fetchReportCount, 60000);
    return () => { alive = false; clearInterval(interval); };
  }, [role]);

  const notifPanelRef = useRef(null);
  const bellBtnRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 72, left: 256 });

  const handleBellToggle = () => {
    if (!notifOpen && bellBtnRef.current) {
      const rect = bellBtnRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 8, left: rect.left });
    }
    setNotifOpen(o => !o);
  };

  useEffect(() => {
    if (!notifOpen) return;
    const handleOutside = (e) => {
      if (
        notifPanelRef.current && !notifPanelRef.current.contains(e.target) &&
        bellBtnRef.current && !bellBtnRef.current.contains(e.target)
      ) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [notifOpen]);

  const showCredits = (role === 'sales_rep' || role === 'admin') && !creditsLoading;
  const creditsLow  = !unlimited && credits !== null && credits < 50;
  const creditsWarn = !unlimited && credits !== null && credits >= 50 && credits < 200;
  const creditsDotColor = creditsLow ? 'bg-red-400' : creditsWarn ? 'bg-yellow-400' : 'bg-green-400';
  const creditsTextColor = creditsLow ? 'text-red-400' : creditsWarn ? 'text-yellow-400' : 'text-green-400';
  const commissionRate = (unlimited || plan === 'pro') ? 25 : plan === 'starter' ? 15 : 10;
  const planTierLabel = (unlimited || plan === 'pro') ? 'Pro Plan' : plan === 'starter' ? 'Starter Plan' : 'Free Plan';

  return (
    <nav className="flex flex-col h-full">
      <div className="flex items-center justify-between border-b border-theme px-5" style={{ height: '72px' }}>
        <a href="/" className="flex flex-col items-start">
          <img
            src="/images/logo.svg"
            alt="QuickGrowth"
            style={{
              height: collapsed ? '30px' : '38px',
              display: 'block',
              filter: 'drop-shadow(0 0 8px rgba(201,168,76,0.45)) drop-shadow(0 0 3px rgba(201,168,76,0.25))',
              transition: 'height 0.2s ease, filter 0.3s ease',
            }}
          />
          {!collapsed && (
            <span
              style={{
                fontSize: '9px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: 'rgba(201,168,76,0.55)',
                marginTop: '3px',
                lineHeight: 1,
              }}
            >
              v1.6.5
            </span>
          )}
        </a>
        <div className="flex items-center gap-1">
          {user?.id && (
            <button
              ref={bellBtnRef}
              onClick={handleBellToggle}
              className={`relative min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg transition-colors ${
                notifOpen ? 'bg-yellow-500/15 text-yellow-400' : 'text-secondary hover:text-primary hover:bg-primary/5'
              }`}
              title="Notifications"
            >
              <svg className="w-[19px] h-[19px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {notifCount > 0 && (
                <span
                  className="absolute top-0.5 right-0.5 flex items-center justify-center rounded-full text-[9px] font-black text-white"
                  style={{
                    minWidth: '15px', height: '15px', padding: '0 3px',
                    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                    boxShadow: '0 0 5px 1px rgba(239,68,68,0.5)',
                  }}
                >
                  {notifCount > 99 ? '99+' : notifCount}
                </span>
              )}
            </button>
          )}
        <button
          onClick={onClose}
          className="lg:hidden text-secondary hover:text-primary min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-primary/5 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3 scrollbar-thin">
        <div className="px-3 mb-2">
          <button
            onClick={() => setBugReportOpen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-yellow-500/25 bg-yellow-500/5 hover:bg-yellow-500/10 hover:border-yellow-500/40 transition-all duration-200 group"
          >
            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
              <svg className="w-[17px] h-[17px] text-yellow-500/70 group-hover:text-yellow-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
            </div>
            {!collapsed && (
              <span className="text-[12px] font-semibold text-yellow-500/70 group-hover:text-yellow-500 transition-colors">
                Report or Suggest Fix
              </span>
            )}
          </button>
        </div>

        {groups.map((group, groupIndex) => (
          <div key={group.label} className={groupIndex > 0 ? 'mt-2' : ''}>
            <div className="px-5 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted">
                {group.label}
              </p>
            </div>
            <ul className="space-y-0.5 px-3">
              {group.items.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === `/dashboard/${roleBasePath[role] || role}`}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `group relative flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 min-h-[42px] ${
                        isActive
                          ? 'bg-yellow-500/10 text-yellow-500'
                          : 'text-secondary hover:bg-primary/[0.04] hover:text-primary'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-yellow-500 rounded-r-full" />
                        )}
                        <svg
                          className={`w-[18px] h-[18px] flex-shrink-0 transition-colors duration-200 ${
                            isActive ? 'text-yellow-500' : 'text-muted group-hover:text-secondary'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={isActive ? 2 : 1.5} d={item.icon} />
                        </svg>
                        {!collapsed && (
                          <span className={`flex-1 text-[13px] transition-colors duration-200 ${
                            isActive ? 'font-semibold' : 'font-medium'
                          }`}>
                            {item.label}
                          </span>
                        )}
                        {item.notifBadge && notifCount > 0 && (
                          <span
                            className="flex-shrink-0 ml-auto text-[9px] font-black rounded-full flex items-center justify-center"
                            style={{
                              minWidth: '16px',
                              height: '16px',
                              padding: '0 4px',
                              background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                              color: '#fff',
                              boxShadow: '0 0 6px 2px rgba(239,68,68,0.4)',
                            }}
                          >
                            {notifCount > 99 ? '99+' : notifCount}
                          </span>
                        )}
                        {item.approvalBadge && dataApprovalCount > 0 && (
                          <span
                            className="flex-shrink-0 ml-auto text-[9px] font-black rounded-full flex items-center justify-center"
                            style={{
                              minWidth: '16px',
                              height: '16px',
                              padding: '0 4px',
                              background: 'linear-gradient(135deg,#C9A84C,#a87d2a)',
                              color: '#000',
                              boxShadow: '0 0 6px 2px rgba(201,168,76,0.45)',
                            }}
                          >
                            {dataApprovalCount > 99 ? '99+' : dataApprovalCount}
                          </span>
                        )}
                        {item.verifyBadge && verifyCount > 0 && (
                          <span
                            className="flex-shrink-0 ml-auto text-[9px] font-black rounded-full flex items-center justify-center"
                            style={{
                              minWidth: '16px',
                              height: '16px',
                              padding: '0 4px',
                              background: 'linear-gradient(135deg,#C9A84C,#a87d2a)',
                              color: '#000',
                              boxShadow: '0 0 6px 2px rgba(201,168,76,0.45)',
                            }}
                          >
                            {verifyCount > 99 ? '99+' : verifyCount}
                          </span>
                        )}
                        {item.reportBadge && reportCount > 0 && (
                          <span
                            className="flex-shrink-0 ml-auto text-[9px] font-black rounded-full flex items-center justify-center"
                            style={{
                              minWidth: '16px',
                              height: '16px',
                              padding: '0 4px',
                              background: 'linear-gradient(135deg,#f97316,#c2410c)',
                              color: '#fff',
                              boxShadow: '0 0 6px 2px rgba(249,115,22,0.4)',
                            }}
                          >
                            {reportCount > 99 ? '99+' : reportCount}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
            {groupIndex < groups.length - 1 && (
              <div className="mx-5 mt-3 border-b border-theme" />
            )}
          </div>
        ))}

        <div className="mx-3 mt-4">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-secondary hover:bg-primary/[0.04] hover:text-primary transition-all duration-200 min-h-[42px]"
          >
            {isDark ? (
              <svg className="w-[18px] h-[18px] flex-shrink-0 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-[18px] h-[18px] flex-shrink-0 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
            {!collapsed && (
              <span className="text-[13px] font-medium">
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </span>
            )}
          </button>
        </div>

        {role === 'sales_rep' && <MotivationalQuote />}
      </div>

      {showUserCard && profile ? (
        <div className="border-t border-theme px-4 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
              <span className="text-yellow-500 text-sm font-bold">
                {profile?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-primary truncate">
                {profile?.full_name || user?.email || 'User'}
              </p>
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-[11px] text-yellow-500/70 font-medium">
                  {roleLabel[role] || role}
                </p>
                {role === 'sales_rep' && !creditsLoading && (
                  <span className="text-[10px] font-bold text-green-400 bg-green-500/10 border border-green-500/20 px-1.5 py-0.5 rounded-full">
                    {commissionRate}% Rev Share
                  </span>
                )}
              </div>
              {role === 'sales_rep' && !creditsLoading && (
                <p className="text-[10px] text-muted font-medium mt-0.5">{planTierLabel}</p>
              )}
              {showCredits && (
                <button
                  onClick={() => navigate(`/dashboard/${role === 'sales_rep' ? 'sales' : role}/pricing`)}
                  className="flex items-center gap-1 mt-1 group/cr"
                  title="View plans & credits"
                >
                  {unlimited ? (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-purple-400 group-hover/cr:text-purple-300 transition-colors">
                        Pro · Unlimited
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${creditsDotColor}`} />
                      <span className={`text-[10px] font-semibold transition-colors group-hover/cr:opacity-80 ${creditsTextColor}`}>
                        {credits !== null ? credits.toLocaleString() : '—'} credits
                      </span>
                      {planInfo?.name && plan !== 'free' && (
                        <span className="text-[10px] text-muted">· {planInfo.name}</span>
                      )}
                      {creditsLow && (
                        <span className="text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 rounded px-1 font-bold">Low</span>
                      )}
                    </>
                  )}
                </button>
              )}
            </div>
            <button
              onClick={onSignOut}
              className="text-muted hover:text-red-400 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg hover:bg-red-500/5 flex-shrink-0"
              title="Sign Out"
            >
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-theme px-4 py-4">
          <div className="text-[11px] text-muted text-center font-medium tracking-wide">
            {roleLabel[role] || role} Portal
          </div>
        </div>
      )}

      {/* ── Notification Dropdown Panel ─────────────────────────────── */}
      {notifOpen && (
        <div
          ref={notifPanelRef}
          className="fixed z-[9999] w-80 rounded-xl border border-theme shadow-2xl overflow-hidden"
          style={{
            top: panelPos.top,
            left: panelPos.left,
            background: 'var(--color-card, #1a1a2e)',
            maxHeight: 'calc(100vh - 120px)',
          }}
        >
          <NotifPanel
            role={role}
            navigate={navigate}
            onClose={() => setNotifOpen(false)}
          />
        </div>
      )}

      <BugReportModal isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />

    </nav>
  );
}

export default DashboardNav;
